<template>
  <Dialog
  :stateId="dialogId"
  maxWidth="500px"
  @dialog-closing="close"
  @close="close">
    <v-card v-if="show" id="bid-outcome-dialog">
      <v-card-title class="primary white--text">
        <span>{{ $t('recordOutcome') }}</span>
        <v-spacer/>
        <BaseDialogActions
        hideRefresh
        hideFullscreen
        customCancel
        elementId="bid-outcome-actions"
        @close="close"/>
      </v-card-title>
      <v-card-text class="pt-4">
        <FormWrapper
        formRef="outcomeForm"
        :buttonText="$t('confirm')"
        :disabled="confirmDisabled || saving"
        @submit="confirm">
          <v-row dense class="mb-3">
            <v-col cols="12" sm="6">
              <v-select
              v-model="rfbStatus"
              :items="rfbStatusOptions"
              item-text="name"
              item-value="value"
              hide-details="auto"
              :label="$t('rfbStatus')"
              data-testid="bid-rfb-status-select"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
              v-model="outcome"
              :items="outcomeOptions"
              item-text="name"
              item-value="value"
              clearable
              hide-details="auto"
              :disabled="!isComplete || !siblingsLoaded"
              :loading="!siblingsLoaded"
              :label="$t('outcome')"
              data-testid="bid-outcome-select">
                <template #item="{item}">
                  <span>{{ item.name }}</span>
                  <Icon
                  v-if="item.disabled && outcomeDisabledReason(item)"
                  icon="mdi-information-outline"
                  iconColor="info"
                  :small="false"
                  class="ml-2"
                  style="pointer-events: auto;"
                  :tooltipText="outcomeDisabledReason(item)"
                  elementId="bid-outcome-disabled-reason"/>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <div v-if="outcomeHint" class="caption text--secondary mt-1" data-testid="bid-outcome-hint">
            {{ outcomeHint }}
          </div>
          <v-text-field
          v-if="reasonApplicable"
          v-model="reason"
          clearable
          :counter="(reason || '').length >= reasonMaxLength ? reasonMaxLength : false"
          :maxlength="reasonMaxLength"
          :label="$t('reason')"
          :rules="reasonRequired ? [rules.required] : []"
          data-testid="bid-reason-input"
          class="mt-3"/>
          <template #left-action>
            <v-btn text @click="close">{{ $t('cancel') }}</v-btn>
          </template>
        </FormWrapper>
      </v-card-text>
    </v-card>

    <v-dialog v-model="closeTractPrompt" width="400" v-if="closeTractPrompt">
      <ConfirmDialog
      :title="$t('closeTract')"
      :body="$t('closeTractAfterLostBody', { tractName: tract.name })"
      :confirmText="$t('closeTract')"
      color="primary"
      @confirm="closeTract"
      @cancel="finish"/>
    </v-dialog>
  </Dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { Outcome, RfbStatus, TractStatus } from '@/utils/Enumerations.js'
import { StringConstants } from '@/utils/constants'
import { uniqueDialogId } from '@/utils/componentHelpers'
import fieldRules from '@/utils/rules.js'
import { isBidOpen } from '@/model/Bid.js'

const CLOSABLE_TRACT_STATUSES = [TractStatus.Initiated.value, TractStatus.Cruised.value, TractStatus.Active.value]

export default {
  name: 'BidOutcomeDialog',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('bid-outcome-dialog'),
    rules: fieldRules.rules,
    reasonMaxLength: StringConstants.ExtraLongStringLength,
    show: false,
    bid: undefined,
    tract: undefined,
    tractBids: [],
    siblingsLoaded: false,
    rfbStatus: undefined,
    outcome: undefined,
    reason: undefined,
    saving: false,
    closeTractPrompt: false
  }),

  computed: {
    offersTractClose () {
      return this.outcome === Outcome.Lost.value &&
        CLOSABLE_TRACT_STATUSES.includes(this.tract?.status) &&
        this.tractBids.length > 0 &&
        this.tractBids.every(b => b.bidId === this.bid.bidId || !isBidOpen(b))
    },

    canWin () {
      return !!this.bid?.tractId && !!this.bid?.supplierAccountId
    },

    conflictingWinnerExists () {
      return this.tractBids.some(b =>
        b.bidId !== this.bid?.bidId &&
        b.outcome === Outcome.Won.value &&
        b.supplierAccountId !== this.bid?.supplierAccountId)
    },

    siblingLostExists () {
      return this.tractBids.some(b =>
        b.bidId !== this.bid?.bidId &&
        b.outcome === Outcome.Lost.value)
    },

    siblingWonExists () {
      return this.tractBids.some(b =>
        b.bidId !== this.bid?.bidId &&
        b.outcome === Outcome.Won.value)
    },

    isComplete () {
      return this.rfbStatus === RfbStatus.Complete.value
    },

    reasonApplicable () {
      return this.rfbStatus === RfbStatus.Withdrawn.value ||
        this.rfbStatus === RfbStatus.Pass.value ||
        (this.isComplete && this.outcome === Outcome.Lost.value)
    },

    outcomeHint () {
      return this.isComplete ? '' : this.$t('completeRfbToRecordOutcome')
    },

    rfbStatusOptions () {
      const persisted = this.bid?.rfbStatus
      const isPassed = persisted === RfbStatus.Pass.value
      return RfbStatus.enums.map(s => ({
        name: s.name,
        value: s.value,
        // Mirrors the server guard: Pass is only reachable from Submitted and is terminal once set.
        disabled: isPassed
          ? s.value !== RfbStatus.Pass.value
          : s.value === RfbStatus.Pass.value && persisted !== RfbStatus.Submitted.value
      }))
    },

    outcomeOptions () {
      return Outcome.enums.map(o => ({
        name: o.name,
        value: o.value,
        disabled:
          (o.value === Outcome.Won.value && (!this.canWin || this.conflictingWinnerExists || this.siblingLostExists)) ||
          (o.value === Outcome.Lost.value && this.siblingWonExists)
      }))
    },

    reasonRequired () {
      return this.outcome === Outcome.Lost.value
    },

    confirmDisabled () {
      if (this.outcome === Outcome.Won.value && (!this.canWin || this.conflictingWinnerExists || this.siblingLostExists)) return true
      if (this.outcome === Outcome.Lost.value && this.siblingWonExists) return true
      return this.reasonRequired && !this.reason?.trim()
    }
  },

  watch: {
    rfbStatus (status) {
      if (status !== RfbStatus.Complete.value) {
        this.outcome = undefined
      }
    }
  },

  methods: {
    ...mapActions('bid', ['updateBid', 'fetchBid', 'fetchBids']),
    ...mapActions('tract', ['fetchTract', 'patchTractStatus']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    outcomeDisabledReason (item) {
      if (item.value === Outcome.Won.value) {
        if (!this.bid?.tractId) return this.$t('wonRequiresTract')
        if (!this.bid?.supplierAccountId) return this.$t('wonRequiresSupplier')
        if (this.conflictingWinnerExists) return this.$t('differentSupplierAlreadyWon')
        if (this.siblingLostExists) return this.$t('wonBlockedByLostSibling')
      } else if (item.value === Outcome.Lost.value && this.siblingWonExists) {
        return this.$t('lostBlockedByWonSibling')
      }
      return ''
    },

    open (bid, tract) {
      this.bid = bid
      this.tract = tract
      this.tractBids = []
      this.siblingsLoaded = !bid.tractId
      this.rfbStatus = bid.rfbStatus
      this.outcome = bid.outcome
      this.reason = bid.reason
      this.show = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })
      if (bid.tractId) this.loadTractBids(bid.tractId)
    },

    async loadTractBids (tractId) {
      try {
        const bids = await this.fetchBids({ tractId })
        if (this.bid?.tractId === tractId) this.tractBids = bids
      } catch {
      } finally {
        if (this.bid?.tractId === tractId) this.siblingsLoaded = true
      }
    },

    async confirm () {
      this.saving = true
      try {
        const full = await this.fetchBid(this.bid.bidId)
        const updated = await this.updateBid({
          bidId: this.bid.bidId,
          payload: {
            tractId: full.tractId,
            name: full.name,
            spot: full.spot,
            destinationAccountId: full.destinationAccountId,
            distance: full.distance,
            supplierAccountId: full.supplierAccountId,
            bidDate: full.bidDate,
            bidExpiration: full.bidExpiration,
            harvestStartDate: full.harvestStartDate,
            harvestEndDate: full.harvestEndDate,
            timberCategoryId: full.timberCategoryId,
            conversionId: full.conversionId,
            landownerTypeId: full.landownerTypeId,
            rfbStatus: this.rfbStatus,
            outcome: this.outcome,
            reason: this.reasonApplicable ? this.reason?.trim() || null : null
          }
        })
        if (updated.tractUpdated === false) this.setSnack(this.$t('bidTractNotUpdated'))
        if (this.offersTractClose) {
          this.closeTractPrompt = true
          return
        }
        this.finish()
      } finally {
        this.saving = false
      }
    },

    async closeTract () {
      this.saving = true
      try {
        const tract = await this.fetchTract(this.tract.tractId)
        await this.patchTractStatus({ tractId: tract.tractId, status: TractStatus.Closed.value, modifiedAt: tract.modifiedAt })
        this.setSnack(this.$t('tractClosed', { tractName: tract.name }))
      } finally {
        this.saving = false
        this.finish()
      }
    },

    finish () {
      this.closeTractPrompt = false
      this.$emit('saved')
      this.close()
    },

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.show = false
    }
  }
}
</script>
